import { useEffect, useState } from 'react';
import styles from '../../styles/styles.module.css';
import callApi from '../../utils/api/callApi';
import Button from '../Button/Button';
import { useAppContext } from '../../contexts/app';

export default function EditCertificates() {
    // The search results Edit link opens this tab as /admin?tab=certificates&order_id=..&certificate_id=..
    const link = new URLSearchParams(window.location.search);
    const [orderId, setOrderId] = useState(link.get('order_id') ?? '');
    // Certificates whose sensor had no batch when batch_id was backfilled have no order; they load by certificate number
    const [certificateNumber, setCertificateNumber] = useState(link.get('order_id') ? '' : (link.get('certificate_id') ?? ''));
    const [certificates, setCertificates] = useState([]);
    const [batchId, setBatchId] = useState('');
    const [certificateId, setCertificateId] = useState('');
    const [edits, setEdits] = useState({});

    const { setPopupMessage } = useAppContext();

    const inBatch = certificates.filter(c => !batchId || c.batch_id === parseInt(batchId));
    const selected = inBatch.filter(c => !certificateId || c.certificate_id === parseInt(certificateId));
    const batchIds = [...new Set(certificates.map(c => c.batch_id))].filter(id => id != null);
    const lookupParams = () => orderId ? { order_id: orderId } : { certificate_id: certificateNumber.replace(/^MNT-?/i, '') };
    const lookupLabel = orderId ? `order ${orderId}` : `certificate ${certificateNumber}`;
    // Only fields every selected certificate has can be edited; the backend rejects the rest
    const fields = selected.length === 0 ? [] : Object.keys(selected[0].generate_certificate_json)
        .filter(key => selected.every(c => key in c.generate_certificate_json))
        .map(key => {
            const values = new Set(selected.map(c => c.generate_certificate_json[key]));
            return { key, value: values.size === 1 ? [...values][0] : null };
        });
    // One row per certificate table line: CD11..CD18 -> "CD1", C1..C3 -> "C"; keys without a trailing digit stand alone
    const rows = [];
    for (const field of fields) {
        const name = field.key.match(/^([A-Za-z]+\d?)\d$/)?.[1] ?? field.key;
        const row = rows.find(r => r.name === name);
        row ? row.fields.push(field) : rows.push({ name, fields: [field] });
    }

    // keepSelection: a reload after saving stays on the batch/certificate that was being edited
    const loadCertificates = (linkedCertificateId = '', keepSelection = false) => {
        // all: superseded certificates too; other callers get only the newest per sensor
        return callApi('get-certificates-by-order-id', { ...lookupParams(), all: true })
            .then(response => {
                if (response.length === 0)
                    setPopupMessage(`No certificates found for ${lookupLabel}`);
                const linked = response.find(c => c.certificate_id === parseInt(linkedCertificateId));
                setCertificates(response);
                if (!keepSelection) {
                    setBatchId(linked ? String(linked.batch_id) : '');
                    setCertificateId(linked ? String(linked.certificate_id) : '');
                }
                setEdits({});
            })
            .catch(err => {
                console.error(err);
                setPopupMessage(`Unable to load certificates for ${lookupLabel}`);
            });
    }

    useEffect(() => {
        if (link.get('order_id') || link.get('certificate_id'))
            loadCertificates(link.get('certificate_id') ?? '');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Save only updates the database; Republish also uploads the PDFs and syncs iMonnit
    const saveCertificates = (changes, publish = false) => {
        const params = { ...lookupParams(), certificate_json: JSON.stringify(changes) };
        if (batchId)
            params.batch_id = batchId;
        if (certificateId)
            params.certificate_id = certificateId;
        if (publish)
            params.publish = 'true';
        return callApi('update-certificates-json', params)
            .then(updated => {
                setPopupMessage(`${publish ? 'Republished' : 'Updated'} ${updated.length} certificate${updated.length === 1 ? '' : 's'}`);
                return loadCertificates('', true);
            })
            .catch(err => {
                console.error(err);
                setPopupMessage(err.response?.data?.error ?? 'Unable to update certificates');
            });
    }

    const handleSave = () => {
        if (Object.keys(edits).length === 0) {
            setPopupMessage('No changes to save');
            return;
        }
        return saveCertificates(edits);
    }

    return (
        <div className={styles.menu}>
            <h1 className={styles.title}>Edit Certificates</h1>
            <form className={`${styles.toolbar} ${styles.center_toolbar}`}onSubmit={event => { event.preventDefault(); loadCertificates(); }}>
                <input className={styles.default_text_box} value={orderId} onChange={event => setOrderId(event.target.value)} placeholder={'Order number'} aria-label='Order number' />
                <input className={styles.default_text_box} value={certificateNumber} onChange={event => setCertificateNumber(event.target.value)} placeholder={'or MNT- number'} aria-label='Certificate number' />
                <Button onClick={() => loadCertificates()} innerText={'Load'} />
                {certificates.length > 0 &&
                    <>
                        <select className={styles.default_text_box} value={batchId} onChange={event => { setBatchId(event.target.value); setCertificateId(''); setEdits({}); }} aria-label='Batch'>
                            <option value=''>All batches</option>
                            {batchIds.map(id => {
                                const cp = certificates.find(c => c.batch_id === id).calibration_procedure_id;
                                return <option key={id} value={id}>Batch {id}{cp ? ` (CP ${cp})` : ''}</option>;
                            })}
                        </select>
                        <select className={styles.default_text_box} value={certificateId} onChange={event => { setCertificateId(event.target.value); setEdits({}); }} aria-label='Certificate'>
                            <option value=''>All certificates</option>
                            {inBatch.map(c => <option key={c.certificate_id} value={c.certificate_id}>MNT-{c.certificate_id} ({c.cable_id ? `lead ${c.cable_id}` : `sensor ${c.sensor_id}`})</option>)}
                        </select>
                        <span>{selected.length} certificate{selected.length === 1 ? '' : 's'}</span>
                    </>
                }
            </form>
            {fields.length > 0 &&
                <>
                    <div className={styles.field_rows}>
                        {rows.map(row => (
                            <div key={row.name} className={styles.field_row}>
                                <span className={styles.field_row_label}>{row.name}</span>
                                <div className={styles.field_row_fields}>
                                    {row.fields.map(field => (
                                        <input
                                            key={field.key}
                                            className={field.key in edits ? `${styles.default_text_box} ${styles.cert_field_edited}` : styles.default_text_box}
                                            aria-label={field.key}
                                            title={field.key}
                                            value={edits[field.key] ?? field.value ?? ''}
                                            placeholder={field.value === null ? '(varies)' : ''}
                                            onChange={event => {
                                                const { [field.key]: _, ...rest } = edits;
                                                // Typing back to the original (or clearing a varying field) drops the change
                                                setEdits(event.target.value === (field.value ?? '') ? rest : { ...rest, [field.key]: event.target.value });
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button onClick={handleSave} innerText={'Save changes'} />
                    <Button onClick={() => saveCertificates({}, true)} innerText={'Republish PDFs'} />
                </>
            }
        </div>
    )
}
